import type { PaceStatus } from "../../lib/revenuePlannerEngine";
import { HEX } from "../../lib/colorSystem";
import { formatCompact } from "@/utils/formatters";

// =============================================================================
// GoalProgressBar — Compact Goal Tracker for Dashboard Header
// =============================================================================
// PURPOSE: Shows all roles their monthly revenue goal progress in one line.
// Visible to closers, setters, dialers, managers, exec, client.
//
// DISPLAY: "Feb Goal: $500K  ████████░░░░░░  $200K (40%)  ⚠️ Behind pace"
// =============================================================================

interface GoalProgressBarProps {
  goalAmount: number;
  cashSoFar: number;
  paceStatus: PaceStatus;
  monthLabel: string; // e.g. "Feb" or "February"
}

const PACE_CONFIG: Record<
  PaceStatus,
  { label: string; color: string; bg: string; icon: string; glow: string }
> = {
  ahead: {
    label: "Ahead of pace",
    color: HEX.green,
    bg: "rgba(16, 185, 129, 0.15)",
    icon: "\u2191",
    glow: "0 0 12px rgba(16, 185, 129, 0.4)",
  },
  on_track: {
    label: "On track",
    color: HEX.accent,
    bg: "rgba(59, 130, 246, 0.15)",
    icon: "\u2192",
    glow: "0 0 12px rgba(59, 130, 246, 0.4)",
  },
  behind: {
    label: "Behind pace",
    color: HEX.white,
    bg: HEX.red,
    icon: "\u2193",
    glow: "0 2px 8px rgba(239, 68, 68, 0.5)",
  },
  no_goal: { label: "", color: "transparent", bg: "transparent", icon: "", glow: "none" },
};

export default function GoalProgressBar({
  goalAmount,
  cashSoFar,
  paceStatus,
  monthLabel,
}: GoalProgressBarProps) {
  if (goalAmount <= 0) return null;

  const pct = Math.min((cashSoFar / goalAmount) * 100, 100);
  const pace = PACE_CONFIG[paceStatus];

  return (
    <div
      className="flex items-center gap-3 rounded-xl liquid-glass-light"
      style={{ padding: "10px 16px" }}
    >
      {/* Label */}
      <span className="font-medium whitespace-nowrap text-glass-secondary" style={{ fontSize: 12 }}>
        {monthLabel} Goal: {formatCompact(goalAmount)}
      </span>

      {/* Progress bar - shows full track + filled portion */}
      <div
        className="flex-1 rounded-full overflow-hidden"
        style={{
          height: 6,
          minWidth: 80,
          maxWidth: 200,
          background: "var(--glass-bg-hover)",
          border: "1px solid var(--glass-bg-active)",
        }}
      >
        <div
          className="h-full rounded-full transition-all duration-500"
          style={{
            width: `${pct}%`,
            background:
              paceStatus === "behind" ? HEX.red : paceStatus === "ahead" ? HEX.green : HEX.accent,
            boxShadow: pace.glow,
          }}
        />
      </div>

      {/* Current value + percentage */}
      <span className="font-semibold whitespace-nowrap text-glass" style={{ fontSize: 12 }}>
        {formatCompact(cashSoFar)}{" "}
        <span className="text-glass-muted" style={{ fontWeight: 400 }}>
          ({pct.toFixed(0)}%)
        </span>
      </span>

      {/* Pace badge */}
      {paceStatus !== "no_goal" && (
        <span
          className="font-medium rounded whitespace-nowrap"
          style={{
            fontSize: 10,
            color: pace.color,
            background: pace.bg,
            padding: "3px 10px",
            textShadow: pace.glow,
          }}
        >
          {pace.icon} {pace.label}
        </span>
      )}
    </div>
  );
}
